import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { resendVerificationEmail } from '../../lib/auth'

interface EmailVerificationProps {
  email: string;
}

export default function EmailVerification({ email }: EmailVerificationProps) {
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  // Listen for email verification completion from other tabs/windows
  useEffect(() => {
    // Listen for localStorage changes (cross-tab communication)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'emailVerified' && e.newValue === 'true') {
        console.log('✅ Email verified in another tab (localStorage)! Redirecting...')
        localStorage.removeItem('emailVerified')
        navigate('/app?setup=true')
      }
    }

    // Listen for postMessage from verification window
    const handleMessage = (event: MessageEvent) => {
      // Security: verify origin
      if (event.origin !== window.location.origin) return

      if (event.data.type === 'EMAIL_VERIFIED') {
        console.log('✅ Email verified (postMessage from child window)! Redirecting...')
        navigate('/app?setup=true')
      } else if (event.data.type === 'EMAIL_VERIFIED_NO_SESSION') {
        console.log('✅ Email verified but no session. User needs to sign in.')
        // The verification window will handle the redirect
        // We could optionally show a message here
      }
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('message', handleMessage)
    }
  }, [navigate])

  const handleResendEmail = async () => {
    if (!user) {
      setError('No user found. Please try registering again.');
      return;
    }

    setIsResending(true);
    setMessage('');
    setError('');

    try {
      await resendVerificationEmail(user);
      setMessage('Verification email sent successfully! Please check your inbox.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col justify-start align-content-center max-w-screen-xl mx-auto overflow-y-auto py-4">
      <div className="mx-auto max-w-md text-center">
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-green-500/10 border border-green-500/20">
          <EnvelopeIcon className="size-6 text-green-400" aria-hidden="true" />
        </div>

        <div className="mt-6">
          <h1 className="text-2xl/9 font-bold tracking-tight text-white">
            Check your email
          </h1>
          <p className="mt-4 text-sm/6 text-gray-400">
            We've sent a verification email to{' '}
            <span className="font-medium text-white">{email}</span>
          </p>
        </div>

        <div className="mt-6">
          <div className="rounded-lg bg-blue-500/10 p-4 border border-blue-500/20">
            <div className="text-sm">
              <p className="text-blue-300">
                Click the verification link in the email to activate your account.
                The link will expire in 24 hours.
              </p>
            </div>
          </div>
        </div>

        {message && (
          <div className="mt-4">
            <div className="rounded-lg bg-green-500/10 p-4 border border-green-500/20">
              <div className="text-sm text-green-300">{message}</div>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4">
            <div className="rounded-lg bg-red-500/10 p-4 border border-red-500/20">
              <div className="text-sm text-red-300">{error}</div>
            </div>
          </div>
        )}

        <div className="mt-6 space-y-4">
          <div>
            <button
              type="button"
              onClick={handleResendEmail}
              disabled={isResending}
              className="w-full justify-center rounded-full bg-gradient-to-r from-purple-600 to-violet-600 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? 'Sending...' : 'Resend verification email'}
            </button>
          </div>

          <div className="text-sm">
            <p className="text-gray-400">
              Didn't receive the email? Check your spam folder.
            </p>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-white/10">
          <p className="text-xs text-gray-400">
            Need help? Contact our support team at{' '}
            <a href="mailto:support@wonderstood.com" className="text-purple-400 hover:text-purple-300">
              support@wonderstood.com
            </a>
          </p>
        </div>

        {/* Dev Mode Button */}
        {import.meta.env.DEV && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <Link
              to="/app"
              className="block w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-all text-center"
            >
              🚀 Dev Mode: Skip to Main App
            </Link>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Development only - bypasses email verification
            </p>
          </div>
        )}
      </div>
    </div>
  );
}